// action-menu.tsx — the single home for a campaign's actions on the detail view
// (and reusable for list-card overflow menus). A generic "⋮" dropdown: each item
// is a label + icon + handler; a `confirm` item requires a second click before it
// fires (for destructive actions like Delete). Theme-aware via the redesign's
// `.campaigns-rd` CSS-var tokens.
//
// The open panel is PORTALED, not absolutely positioned inside the row. As an
// in-row absolute box it was painted by its own card's stacking order, so the
// NEXT card down — a later sibling — drew on top of it: the menu appeared to
// slide *under* the following row's "⋯" button and card body. Portaling it out
// (fixed coordinates measured from the trigger) takes it out of the card
// stacking order entirely, and also stops any `overflow:hidden` on a card or
// scroller from clipping it.
//
// The portal target is the nearest `.campaigns-rd` ancestor rather than
// `document.body`: every colour here is a CSS var defined on that wrapper (and
// the dark theme is a `data-theme` attribute on it), so a body-level portal
// would render an unstyled white-on-white panel. `document.body` is only a
// fallback, and then the panel carries the `campaigns-rd` class itself.

import React from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';

export interface ActionItem {
  key: string;
  label: string;
  icon: LucideIcon;
  /** Danger styling (red) — for destructive actions. */
  danger?: boolean;
  /** Require a second click ("Click again to confirm") before firing onSelect. */
  confirm?: boolean;
  onSelect: () => void;
}

const MENU_WIDTH = 200;
const ITEM_HEIGHT = 34;
const MENU_PADDING = 10;
const GAP = 6;
const VIEWPORT_MARGIN = 8;

interface MenuPosition {
  top: number;
  left: number;
}

export const ActionsMenu: React.FC<{ items: ActionItem[]; align?: 'left' | 'right' }> = ({
  items,
  align = 'right',
}) => {
  const { t } = useTranslation(namespace);
  const [open, setOpen] = React.useState(false);
  const [hovered, setHovered] = React.useState<string | null>(null);
  const [confirmingKey, setConfirmingKey] = React.useState<string | null>(null);
  const [pos, setPos] = React.useState<MenuPosition | null>(null);
  const [host, setHost] = React.useState<HTMLElement | null>(null);

  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const close = React.useCallback(() => {
    setOpen(false);
    setConfirmingKey(null);
    setHovered(null);
    setPos(null);
  }, []);

  const openMenu = () => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const height = items.length * ITEM_HEIGHT + MENU_PADDING;

    // Flip above the trigger when the panel would run off the bottom — the
    // usual case for the last card in a long list.
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < height + GAP + VIEWPORT_MARGIN
      ? Math.max(VIEWPORT_MARGIN, rect.top - GAP - height)
      : rect.bottom + GAP;

    // `align` is expressed in FLOW terms: 'right' = the menu's END edge lines up
    // with the trigger's END edge. In an RTL layout the end edge is on the LEFT,
    // so the anchor flips — otherwise the panel hangs off the wrong side of the
    // "⋯" button. The resulting `left` is a measured VIEWPORT coordinate fed to
    // `position: fixed`, so it stays physical on purpose (an inset-inline-start
    // here would be measured from the opposite edge and misplace the panel).
    const rtl = getComputedStyle(trigger).direction === 'rtl';
    const alignToEnd = align === 'right';
    const anchorToLeftEdge = rtl ? alignToEnd : !alignToEnd;
    const rawLeft = anchorToLeftEdge ? rect.left : rect.right - MENU_WIDTH;
    const left = Math.min(
      Math.max(VIEWPORT_MARGIN, rawLeft),
      Math.max(VIEWPORT_MARGIN, window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN),
    );

    setHost(trigger.closest('.campaigns-rd') as HTMLElement | null);
    setPos({ top, left });
    setOpen(true);
  };

  // Fixed coordinates go stale the moment anything scrolls (and the list this
  // lives in is virtualized), so close rather than chase the trigger.
  React.useEffect(() => {
    if (!open) return undefined;
    const onDismiss = () => close();
    window.addEventListener('scroll', onDismiss, true);
    window.addEventListener('resize', onDismiss);
    return () => {
      window.removeEventListener('scroll', onDismiss, true);
      window.removeEventListener('resize', onDismiss);
    };
  }, [open, close]);

  const handleClick = (item: ActionItem) => {
    if (item.confirm && confirmingKey !== item.key) {
      setConfirmingKey(item.key);
      return;
    }
    close();
    item.onSelect();
  };

  const panel = open && pos && (
    // `campaigns-rd` on the panel only when it had to fall back to the body —
    // inside the real wrapper the class (and its data-theme) is inherited.
    <div className={host ? undefined : 'campaigns-rd'}>
      {/* click-outside scrim */}
      <div onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 2000 }} />
      <div
        role="menu"
        style={{
          position: 'fixed',
          top: pos.top,
          left: pos.left,
          width: MENU_WIDTH,
          zIndex: 2001,
          padding: 5,
          background: 'var(--surface)',
          border: '1px solid var(--line)',
          borderRadius: 10,
          boxShadow: '0 10px 30px rgba(11,18,32,.16)',
        }}
      >
        {items.map((item) => {
          const Icon = item.icon;
          const confirming = confirmingKey === item.key;
          const active = hovered === item.key;
          const danger = item.danger || confirming;
          return (
            <button
              key={item.key}
              type="button"
              role="menuitem"
              onMouseEnter={() => setHovered(item.key)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => handleClick(item)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                width: '100%',
                padding: '8px 10px',
                border: 'none',
                borderRadius: 7,
                background: active
                  ? danger
                    ? 'var(--escalate-soft)'
                    : 'var(--surface-2)'
                  : 'transparent',
                color: danger ? 'var(--escalate)' : 'var(--ink)',
                font: 'inherit',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                textAlign: 'start',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon size={14} /> {confirming ? t('campaignsRedesign.actionMenu.confirm', 'Click again to confirm') : item.label}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={triggerRef}
        type="button"
        className="btn ghost icon"
        onClick={() => (open ? close() : openMenu())}
        aria-label={t('campaignsRedesign.actionMenu.trigger', 'More actions')}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal size={16} />
      </button>
      {panel && createPortal(panel, host ?? document.body)}
    </div>
  );
};

export default ActionsMenu;
